import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useRef, useState } from 'react';
import { Api } from '../../../services/api/axios-config';
import { setCsrfRequestToken } from '../../../services/api/axios-config/interceptors';
import { feedback, finishRequest } from '../../../services';
import { Button } from '../../MUI/button/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface FileUploaderProps {
  open: boolean;
  id: number;
  refetch: () => void;
  uploadFile: (id: number, file: FormData) => Promise<string>;
  deleteFile: (id: number) => Promise<string>;
  onDelete: () => void;
  onClose: () => void;
  /** Called with the uploaded file URL so the parent can update local state immediately */
  onUploadSuccess?: (url: string) => void;
}

export const FileUploader: React.FC<FileUploaderProps> = ({ open, id, uploadFile, deleteFile, onDelete, refetch, onClose, onUploadSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | undefined>();

  const { mutate: upload, isLoading: isLoadingUpload } = useMutation((formData: FormData) =>
    uploadFile(id, formData), {
    onSuccess: (url) => {
      onUploadSuccess?.(url);
      finishRequest('Salvo com sucesso', 'success', () => {
        refetch();
        setFile(undefined);
        onClose();
      });
    },
    onError: (error: any) => finishRequest(String(error), 'error'),
  });

  const { mutate: remove, isLoading: isLoadingDelete } = useMutation(() =>
    deleteFile(id), {
    onSuccess: () => finishRequest('Deletado com sucesso', 'success', () => {
      refetch();
      setFile(undefined);
      onClose();
    }),
    onError: (error: any) => finishRequest(String(error), 'error'),
  });

  const handleChangeInputFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];

    if (selectedFile && selectedFile.size > 2 * 1024 * 1024) {
      feedback('Arquivo muito grande. Tamanho máximo permitido: 2MB', 'error');
      e.target.value = '';
      setFile(undefined);
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = useCallback(async () => {
    if (!file) {
      feedback('Selecione uma foto primeiro!', 'error');
      return;
    }

    const formData = new FormData();

    formData.append('file', file);

    try {
      const tokenRes = await Api.get('/auth/antiforgery-token');
      if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      upload(formData);
    } catch (error) {
      console.error('Erro ao salvar arquivo.', error);
    }
  }, [file, upload]);

  const handleDelete = useCallback(async () => {
    onDelete();

    try {
      const tokenRes = await Api.get('/auth/antiforgery-token');
      if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      remove();
    } catch (error) {
      console.error('Erro ao deletar arquivo.', error);
    }
  }, [onDelete, remove]);

  const handleClickUploadArea = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <DialogContent sx={{ minWidth: 360 }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={handleChangeInputFile}
          style={{ display: 'none' }}
        />

        {/* Styled upload area */}
        <Box
          onClick={handleClickUploadArea}
          sx={{
            border: '2px dashed',
            borderColor: file ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: file ? 'rgba(236, 64, 122, 0.04)' : 'grey.50',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'rgba(236, 64, 122, 0.04)',
            },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: file ? 'primary.main' : 'grey.400', mb: 1 }} />
          <Typography variant='body1' color={file ? 'primary.main' : 'text.secondary'}>
            {file ? file.name : 'Clique para selecionar uma foto'}
          </Typography>
          <Typography variant='caption' color='text.disabled'>
            PNG ou JPEG • Máx 2MB
          </Typography>
        </Box>

        <Box
          display='flex'
          justifyContent='center'
          marginTop={2}
          gap={2}
        >
          <Button
            label='Salvar'
            isLoading={isLoadingUpload}
            onClick={handleUpload}
            variant='contained'
            disabled={!file}
          />

          <Button
            label='Excluir foto'
            isLoading={isLoadingDelete}
            onClick={handleDelete}
            variant='outlined'
            color='error'
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
